import { pickActiveEvmWallet, connectEvmWallet } from './detectEvmWallets';
import { EVM_WALLET_SIGN_MSG, evmWalletMismatchMsg } from './evmWalletMessages';
import { signHlUserTypedData, formatHlSignNetworkError } from './hlSignNetwork';
import { parseApiError } from './hlDepositMessages';

/**
 * Withdraw USDC from Hyperliquid to Arbitrum (browser wallet signature).
 * Requires the master wallet — Omni agent keys cannot withdraw.
 */
export async function withdrawUsdcFromHlWithBrowserWallet({
  walletAddress,
  amountUsd,
  destination,
  apiBase,
  authHeaders,
}) {
  const w = await pickActiveEvmWallet();
  if (!w) {
    throw new Error(
      `${EVM_WALLET_SIGN_MSG} Use the same 0x address linked in Omni (MetaMask, Rabby, Phantom Ethereum, etc.).`
    );
  }

  let address;
  try {
    ({ address } = await connectEvmWallet(w.provider));
  } catch (e) {
    throw new Error(formatHlSignNetworkError(e));
  }

  if (walletAddress && address.toLowerCase() !== String(walletAddress).trim().toLowerCase()) {
    throw new Error(evmWalletMismatchMsg(address));
  }

  const axios = (await import('axios')).default;
  let prepared;
  try {
    const { data } = await axios.post(
      `${apiBase}/hyperliquid/withdraw/prepare`,
      {
        walletAddress: address,
        amountUsd,
        destination: destination || address,
      },
      { headers: authHeaders() }
    );
    prepared = data;
  } catch (e) {
    throw new Error(parseApiError(e, address));
  }

  let signature;
  try {
    signature = await signHlUserTypedData(w.provider, address, prepared.typedData);
  } catch (e) {
    throw new Error(formatHlSignNetworkError(e));
  }

  try {
    const { data: submitted } = await axios.post(
      `${apiBase}/hyperliquid/withdraw/submit`,
      {
        action: prepared.action,
        nonce: prepared.nonce,
        signature,
        walletAddress: address,
        amountUsd: prepared.amountUsd,
      },
      { headers: authHeaders() }
    );

    if (!submitted?.success) {
      throw new Error(submitted?.error || 'Withdrawal failed');
    }

    return {
      ...submitted,
      amountUsd: prepared.amountUsd,
      destination: prepared.destination,
      feeNote: prepared.feeNote,
      balances: submitted.balances || null,
      verified: !!submitted.verified,
    };
  } catch (e) {
    throw new Error(parseApiError(e, address));
  }
}
